<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import * as THREE from 'three'
import SceneGroupCard from './SceneGroupCard.vue'

type TransformAxis = 'x' | 'y' | 'z'

const TRANSFORM_AXES: TransformAxis[] = ['x', 'y', 'z']

const props = withDefaults(
  defineProps<{
    object: THREE.Object3D | null
    pivot?: THREE.Vector3 | null
    title?: string
    subtitle?: string
    worldAxesVisible?: boolean
  }>(),
  {
    pivot: null,
    title: '模型变换',
    subtitle: '调整模型节点的位置、旋转和统一缩放',
    worldAxesVisible: false,
  },
)

const emit = defineEmits<{
  changed: [object: THREE.Object3D]
  'update:worldAxesVisible': [visible: boolean]
}>()

const position = reactive<Record<TransformAxis, number>>({ x: 0, y: 0, z: 0 })
const rotation = reactive<Record<TransformAxis, number>>({ x: 0, y: 0, z: 0 })
const uniformScale = reactive({ value: 1 })

const isDisabled = computed(() => props.object === null)
const badge = computed(() => (props.object ? '已加载' : '无模型'))

let isSyncingFromObject = false

watch(
  () => [props.object, props.pivot] as const,
  () => {
    syncFromObject()
  },
  { immediate: true },
)

function syncFromObject(): void {
  isSyncingFromObject = true

  const object = props.object
  if (!object) {
    position.x = 0
    position.y = 0
    position.z = 0
    rotation.x = 0
    rotation.y = 0
    rotation.z = 0
    uniformScale.value = 1
    isSyncingFromObject = false
    return
  }

  const pivotPosition = getPivotPosition(object)
  position.x = roundNumber(pivotPosition.x)
  position.y = roundNumber(pivotPosition.y)
  position.z = roundNumber(pivotPosition.z)
  rotation.x = roundNumber(THREE.MathUtils.radToDeg(object.rotation.x))
  rotation.y = roundNumber(THREE.MathUtils.radToDeg(object.rotation.y))
  rotation.z = roundNumber(THREE.MathUtils.radToDeg(object.rotation.z))
  uniformScale.value = roundNumber(object.scale.x || 1)
  isSyncingFromObject = false
}

function normalizeFiniteNumber(value: unknown, fallback: number): number {
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

function roundNumber(value: unknown, digits = 3): number {
  return Number(normalizeFiniteNumber(value, 0).toFixed(digits))
}

function getPivotPosition(object: THREE.Object3D): THREE.Vector3 {
  const pivot = props.pivot
  if (!pivot) {
    return object.position
  }

  object.updateMatrixWorld(true)
  const worldPivot = object.localToWorld(pivot.clone())
  return object.parent ? object.parent.worldToLocal(worldPivot) : worldPivot
}

function applyTransform(): void {
  if (isSyncingFromObject) {
    return
  }

  const object = props.object
  if (!object) {
    return
  }

  position.x = roundNumber(position.x)
  position.y = roundNumber(position.y)
  position.z = roundNumber(position.z)
  rotation.x = roundNumber(rotation.x)
  rotation.y = roundNumber(rotation.y)
  rotation.z = roundNumber(rotation.z)
  uniformScale.value = Math.max(0.001, roundNumber(uniformScale.value))

  const euler = new THREE.Euler(
    THREE.MathUtils.degToRad(rotation.x),
    THREE.MathUtils.degToRad(rotation.y),
    THREE.MathUtils.degToRad(rotation.z)
  )
  const quaternion = new THREE.Quaternion().setFromEuler(euler)

  if (props.pivot) {
    const matrix = new THREE.Matrix4()
      .compose(
        new THREE.Vector3(position.x, position.y, position.z),
        quaternion,
        new THREE.Vector3(uniformScale.value, uniformScale.value, uniformScale.value)
      )
      .multiply(new THREE.Matrix4().makeTranslation(-props.pivot.x, -props.pivot.y, -props.pivot.z))
    object.matrix.copy(matrix)
    object.matrix.decompose(object.position, object.quaternion, object.scale)
  } else {
    object.position.set(position.x, position.y, position.z)
    object.quaternion.copy(quaternion)
    object.scale.setScalar(uniformScale.value)
  }
  object.updateMatrixWorld(true)
  emit('changed', object)
}

function resetTransform(): void {
  position.x = 0
  position.y = 0
  position.z = 0
  rotation.x = 0
  rotation.y = 0
  rotation.z = 0
  uniformScale.value = 1
  applyTransform()
}

function toggleWorldAxes(): void {
  emit('update:worldAxesVisible', !props.worldAxesVisible)
}
</script>

<template>
  <SceneGroupCard :title="title" :subtitle="subtitle" :badge="badge">
    <div class="object-transform-card">
      <div class="transform-row">
        <span class="transform-row-label">位置</span>
        <label v-for="axis in TRANSFORM_AXES" :key="`position-${axis}`" class="transform-axis-field">
          <span>{{ axis }}</span>
          <input
            v-model.number="position[axis]"
            class="panel-input transform-number-input"
            type="number"
            step="0.1"
            :disabled="isDisabled"
            @input="applyTransform"
            @change="applyTransform"
          />
        </label>
      </div>

      <div class="transform-row">
        <span class="transform-row-label">旋转</span>
        <label v-for="axis in TRANSFORM_AXES" :key="`rotation-${axis}`" class="transform-axis-field">
          <span>{{ axis }}</span>
          <input
            v-model.number="rotation[axis]"
            class="panel-input transform-number-input"
            type="number"
            step="1"
            :disabled="isDisabled"
            @input="applyTransform"
            @change="applyTransform"
          />
        </label>
      </div>

      <div class="transform-row">
        <span class="transform-row-label">缩放</span>
        <label class="transform-axis-field transform-scale-field">
          <input
            v-model.number="uniformScale.value"
            class="panel-input transform-number-input"
            type="number"
            min="0.001"
            step="0.05"
            :disabled="isDisabled"
            @input="applyTransform"
            @change="applyTransform"
          />
        </label>
        <button
          class="panel-button panel-button-subtle transform-reset-button"
          type="button"
          :disabled="isDisabled"
          @click="resetTransform"
        >
          重置
        </button>
      </div>

      <button
        class="panel-button panel-button-subtle transform-axes-button"
        type="button"
        @click="toggleWorldAxes"
      >
        <span>{{ props.worldAxesVisible ? '关闭世界坐标轴' : '打开世界坐标轴' }}</span>
        <span class="transform-axes-legend" aria-label="X 红色，Y 绿色，Z 蓝色">
          <span class="transform-axes-item">
            <span class="transform-axes-dot transform-axes-dot-x" aria-hidden="true"></span>
            X
          </span>
          <span class="transform-axes-item">
            <span class="transform-axes-dot transform-axes-dot-y" aria-hidden="true"></span>
            Y
          </span>
          <span class="transform-axes-item">
            <span class="transform-axes-dot transform-axes-dot-z" aria-hidden="true"></span>
            Z
          </span>
        </span>
      </button>
    </div>
  </SceneGroupCard>
</template>

<style scoped>
.object-transform-card {
  display: grid;
  gap: 10px;
}

.transform-row {
  display: grid;
  grid-template-columns: 42px repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: 8px;
}

.transform-row-label {
  color: #c7d5e6;
  font-size: 12px;
  font-weight: 800;
}

.transform-axis-field {
  min-width: 0;
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr);
  align-items: center;
  gap: 4px;
  color: #92a7bd;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.transform-scale-field {
  grid-column: span 2;
  grid-template-columns: minmax(0, 1fr);
}

.transform-number-input {
  width: 100%;
  min-width: 0;
  height: 34px;
  padding: 0 8px;
  font-size: 12px;
}

.transform-reset-button {
  min-width: 0;
  min-height: 34px;
  padding: 0 10px;
}

.transform-axes-button {
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.transform-axes-legend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #aebdd0;
  font-size: 11px;
  font-weight: 800;
}

.transform-axes-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.transform-axes-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
}

.transform-axes-dot-x {
  background: #ff4d4f;
}

.transform-axes-dot-y {
  background: #52c41a;
}

.transform-axes-dot-z {
  background: #4096ff;
}

@media (max-width: 520px) {
  .transform-row {
    grid-template-columns: 38px repeat(3, minmax(0, 1fr));
    gap: 6px;
  }

  .transform-axis-field {
    grid-template-columns: 10px minmax(0, 1fr);
  }

  .transform-number-input {
    padding: 0 6px;
  }
}
</style>
